/**
 * WASI preview1 `errno`, and the error a filesystem call fails with.
 *
 * These live here rather than in idfweb-wasix because a filesystem that could
 * not say what it refused would need a second vocabulary of its own, and every
 * caller would then need a table mapping one onto the other. The numbers are
 * WASI's because WASI is what the guest is going to be told, and a mapping in
 * between could only lose detail.
 */

/** Error codes used by idfweb. */
export const Errno = {
    SUCCESS: 0,
    ACCES: 2,
    AGAIN: 6,
    BADF: 8,
    CHILD: 12,
    EXIST: 20,
    FAULT: 21,
    INVAL: 28,
    IO: 29,
    ISDIR: 31,
    LOOP: 32,
    MFILE: 33,
    NAMETOOLONG: 37,
    NFILE: 41,
    NOENT: 44,
    NOEXEC: 45,
    NOMEM: 48,
    NOSPC: 51,
    NOSYS: 52,
    NOTDIR: 54,
    NOTEMPTY: 55,
    NOTSUP: 58,
    NOTTY: 59,
    OVERFLOW: 61,
    PERM: 63,
    PIPE: 64,
    RANGE: 68,
    ROFS: 69,
    SPIPE: 70,
    XDEV: 75,
    NOTCAPABLE: 76,
} as const;

export type Errno = (typeof Errno)[keyof typeof Errno];

/**
 * A call that failed for a reason the guest is entitled to hear about.
 *
 * Thrown by every operation in this package, and by the syscalls in
 * idfweb-wasix, which turn it back into the number the guest expects. Without
 * a stack trace: an errno is an answer to the guest, not a failure.
 */
export class WasiError extends Error {
    readonly errno: Errno;

    constructor(errno: Errno) {
        const v8 = Error as unknown as { stackTraceLimit?: number }; // absent outside V8
        const limit = v8.stackTraceLimit;
        v8.stackTraceLimit = 0;
        super(`errno ${errno}`);
        v8.stackTraceLimit = limit;
        this.errno = errno;
    }
}

/** Whether `e` is a WasiError carrying this particular errno. */
export function isErrno(e: unknown, errno: Errno): boolean {
    return e instanceof WasiError && e.errno === errno;
}
